/**
 * Demo environment utility to replace company names in the UI.
 *
 * When REACT_APP_DEMO_COMPANY_NAME_REPLACEMENTS is set, this module will:
 * 1. Parse pairs from the CSV: "from1, to1, from2, to2, ..."
 * 2. Replace all occurrences of each "from" text with its corresponding "to" text
 * 3. Observe DOM mutations to replace text in dynamically added content
 *
 * Example: "TestCo, Company Name, ChildCo1, Subsidiary1, ChildCo2, Subsidiary2"
 * Maps: TestCo => Company Name, ChildCo1 => Subsidiary1, ChildCo2 => Subsidiary2
 *
 * This is a hack for demo environments - do not use in production.
 */
import config from 'src/config';

interface ReplacementPair {
  regex: RegExp;
  replacement: string;
}

function replaceTextInNode(node: Node, pairs: ReplacementPair[]) {
  if (node.nodeType === Node.TEXT_NODE) {
    if (node.nodeValue) {
      let newValue = node.nodeValue;
      for (const { regex, replacement } of pairs) {
        // Reset lastIndex since we're using 'g' flag
        regex.lastIndex = 0;
        newValue = newValue.replace(regex, replacement);
      }
      if (newValue !== node.nodeValue) {
        node.nodeValue = newValue;
      }
    }
    return;
  }

  if (
    node.nodeType === Node.ELEMENT_NODE &&
    !['SCRIPT', 'STYLE', 'NOSCRIPT'].includes((node as Element).tagName)
  ) {
    for (const child of node.childNodes) {
      replaceTextInNode(child, pairs);
    }
  }
}

function runReplacement(pairs: ReplacementPair[]) {
  if (!document.body) return;
  replaceTextInNode(document.body, pairs);
}

function observeMutations(pairs: ReplacementPair[]) {
  if (!document.body) return;

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        replaceTextInNode(node, pairs);
      });
      if (m.type === 'characterData' && m.target) {
        replaceTextInNode(m.target, pairs);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    characterData: true,
    subtree: true,
  });
}

/**
 * Parse the CSV string into replacement pairs.
 * Format: "from1, to1, from2, to2, ..."
 * Returns array of { regex, replacement } objects.
 */
function parseReplacementPairs(csv: string): ReplacementPair[] {
  const parts = csv.split(',').map((s) => s.trim());
  const pairs: ReplacementPair[] = [];

  // Process pairs (every 2 elements)
  for (let i = 0; i < parts.length - 1; i += 2) {
    const from = parts[i];
    const to = parts[i + 1];

    if (from && to) {
      pairs.push({
        regex: new RegExp(escapeRegExp(from), 'gi'),
        replacement: to,
      });
    }
  }

  return pairs;
}

/**
 * Initialize the demo company name replacer.
 * Call this early in the app lifecycle (e.g., in index.tsx).
 */
export function initDemoCompanyNameReplacer() {
  const replacementsConfig = config.demoCompanyNameReplacements;

  // Only run if the env var is set
  if (!replacementsConfig) {
    return;
  }

  const pairs = parseReplacementPairs(replacementsConfig);

  if (pairs.length === 0) {
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      runReplacement(pairs);
      observeMutations(pairs);
    });
  } else {
    runReplacement(pairs);
    observeMutations(pairs);
  }
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
